/**
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 *
 * THIS FILE IS MANAGED CENTRALLY BY THE `common-code` REPO.
 * IT COULD BE AUTO-REPLACED AT ANY TIME.
 * DO NOT MAKE CUSTOM CHANGES TO THIS FILE.
 * @see https://gitlab.com/dea-aero/development/common-code
 *
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import Stack from "@mui/material/Stack"
import Box from "@mui/material/Box";
import {Chip, Link} from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import { GridLogicOperator } from '@mui/x-data-grid-pro'

import {cellDefaults, DataGrid} from "../part/DataGrid";
import {useLoginStore} from "../useLoginStore";
import {useApiCallback} from "../util/Api";


const cmp = (a, b) => a === b ? 0 : (`${a}` < `${b}` ? -1 : 1)

const UserInfoLink = ({email, name, navigate}) => {
    return <Link color="inherit"
                 sx={{
                     lineHeight: '160%',
                     textDecoration: 'none',
                     '&:hover': {textDecoration: 'underline'},
                     cursor: 'pointer'
                 }}
                 onClick={() => navigate(`/person/${email}`)}
    >{name}</Link>
}

function EnabledChip({enabled}) {
    const props = {
        sx: {width:70, m:'0.5px', mr:2},
        label: enabled? 'Enabled' : 'Disabled',
        size: 'small',
    }
    if (enabled) {
        props.color = "success"
    }
    else {
        props.color = "error"
    }
    return <Chip {...props}/>
}

export default function People() {
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()
    const canUser = useLoginStore(state => state.canUser)
    const canUserSeeUsers = canUser('GET', '/api/users')
    const [email, setEmail] = useState('')
    const [name, setName] = useState('')
    const [users, setUsers] = useState([]);

    const gotUsers = useCallback(users => {
        users.forEach(u => {
            u.logintype = u.userid?.split('-')[0] || 'password';
            if (u.email.indexOf('@')) {
                u.domain = u.email?.split('@')[1]
            } else {
                u.domain = ''
            }
            u.id = u.email;
        })
        setUsers(users)
    }, [setUsers])

    const getUsers = useApiCallback('GET', '/api/users', {setLoading, onDone: gotUsers})

    useEffect(() => {
        setUsers([])
        if (canUserSeeUsers) getUsers()
    }, [canUserSeeUsers, getUsers]);

    const navigateToPerson = useCallback(() => navigate(`/person/${email}`), [navigate, email])
    const createUser = useApiCallback('PUT', `/api/user/create/${email}`, {setLoading, data: {}, onDone: navigateToPerson})

    const sorted = useMemo(() => (
        [...users].sort(
            (a, b) => a.logintype !== b.logintype ? cmp(b.logintype, a.logintype) : cmp(a.name, b.name)
        )
    ), [users])

    const filtered = useMemo(() => {
        const _name = name.toLowerCase()
        const _email = email.toLowerCase()
        return sorted.filter(
            user => (user.email.indexOf(_email) > -1 || user.name.indexOf(_name) > -1)
        )
    }, [sorted, email, name])

    return <Container><Stack sx={{fontSize:{xs:'0.8em',sm:'0.9em',md:'1em'}}}>
        <Box container>
            <Stack direction="row" alignItems="center" spacing={2}>
                <TextField
                    label="Username"
                    disabled={loading}
                    InputLabelProps={{shrink: true}}
                    value={email}
                    inputProps={{autoComplete:"none"}}
                    onChange={(event) => {
                        setEmail(event.target.value)
                    }}
                    sx={{width: '300px', margin: 2}}
                />
                <TextField
                    label="Display Name"
                    disabled={loading}
                    InputLabelProps={{shrink: true}}
                    value={name}
                    inputProps={{autoComplete:"none"}}
                    onChange={(event) => {
                        setName(event.target.value)
                    }}
                    sx={{width: '300px', margin: 2}}
                />
                <Button
                    onClick={() => createUser({name})}
                >Create Internal User</Button>
            </Stack>

            <DataGrid
                wrapperStyle={{height: 'calc(100dvh - 140px)'}}
                loading={loading}
                rows={filtered}
                columns={[
                    {field: 'user_type', headerName: 'User Type', flex: 4, ...cellDefaults},
                    {field: 'logintype', headerName: 'Login Type', flex: 4, ...cellDefaults},
                    {field: 'domain', headerName: 'Domain', flex: 4,...cellDefaults},
                    {field: 'orgunit', headerName: 'Org Unit', flex: 4,...cellDefaults},
                    {field: 'callsign', headerName: 'Callsign', flex: 4,...cellDefaults},
                    {field: 'active', headerName: 'Enabled', type: 'boolean', flex: 4, ...cellDefaults,
                        renderCell: cell => <EnabledChip enabled={cell?.value}/>,
                    },
                    {field: 'email', headerName: 'Username', flex: 4, ...cellDefaults,
                        renderCell: cell => <UserInfoLink email={cell?.row?.email} name={cell?.row?.email} navigate={navigate}/>,
                    },
                    {field: 'name', headerName: 'Name', flex: 4, ...cellDefaults,
                        headerAlign: 'left',
                        align: 'left',
                        renderCell: cell => <UserInfoLink email={cell?.row?.email} name={cell?.row?.name} navigate={navigate}/>,
                    },
                ]}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            user_type: false,
                            orgunit: false,
                            callsign: false,
                        },
                    },
                    sorting: {
                        sortModel: [
                            {field: 'logintype', sort: 'desc'},
                        ],
                    },
                    filter: {
                        filterModel: {
                            items: [
                                {field: 'logintype', operator: 'doesNotEqual', value: 'google'},
                            ],
                            logicOperator: GridLogicOperator.And,
                        }
                    }
                }}
            />
        </Box>
    </Stack></Container>
}
